import { useState, useEffect } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BottomNav from '../components/BottomNav';
import Header from '../components/Header';
import ShopSetup from '../components/shop/ShopSetup';
import ProductList from '../components/shop/ProductList';
import Analytics from '../components/shop/Analytics';
import ShopClosureControl from '../components/shop/ShopClosureControl';
import TemporaryLocationsControl from '../components/shop/TemporaryLocationsControl';
import useShopTools from '../hooks/useShopTools';
import { Store, Package, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { user, loading: authLoading, checkAuth } = useAuth();
  const { isEnabled } = useShopTools();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('prodotti');

  useEffect(() => {
    // If user data was passed from AuthCallback, use it
    if (routerLocation.state?.user && !user) {
      checkAuth();
    }
  }, [routerLocation.state, user, checkAuth]);

  useEffect(() => {
    if (user) {
      fetchShop();
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/negoziante');
    }
  }, [authLoading, user, navigate]);

  const fetchShop = async () => {
    try {
      const response = await axios.get(`${API}/shops/my`, { withCredentials: true });
      setShop(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setShop(null);
      } else {
        console.error('Error fetching shop:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShopCreated = async () => {
    // Reload the shop from the API to avoid passing a mutable object back.
    try {
      const res = await axios.get(`${API}/shops/my`, { withCredentials: true });
      setShop(res.data);
    } catch (err) {
      // Non-blocking: dashboard already shows old shop; log for debugging.
      console.debug('Reload shop after create failed:', err?.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-safe" data-testid="dashboard-page">
      <Header />

      <main className="px-4 py-4 max-w-7xl mx-auto">
        {shop && (
          <div className="mb-3 flex items-center gap-3">
            <h1 className="font-outfit font-bold text-xl text-slate-800 truncate" data-testid="dashboard-shop-name">
              {shop.nome}
            </h1>
          </div>
        )}
        {!shop ? (
          <ShopSetup onShopCreated={handleShopCreated} />
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6 bg-slate-100 p-1 rounded-xl">
              <TabsTrigger 
                value="prodotti" 
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-prodotti"
              >
                <Package className="w-4 h-4 mr-2" />
                {shop?.business_category === 'tecnico' ? 'Servizi' : 'Prodotti'}
              </TabsTrigger>
              <TabsTrigger 
                value="negozio"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-negozio"
              >
                <Store className="w-4 h-4 mr-2" />
                Negozio
              </TabsTrigger>
              <TabsTrigger 
                value="analytics"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-analytics"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>

            <TabsContent value="prodotti">
              <ProductList shop={shop} />
            </TabsContent>

            <TabsContent value="negozio">
              <div className="space-y-4">
                <ShopClosureControl
                  closureUntil={shop?.closure_until}
                  onChange={(closure_until) => setShop((s) => (s ? { ...s, closure_until } : s))}
                />
                {isEnabled('posizione_temporanea') && (
                  <TemporaryLocationsControl
                    locations={shop?.temporary_locations || []}
                    onChange={(locations) => setShop((s) => (s ? { ...s, temporary_locations: locations } : s))}
                  />
                )}
                <ShopSetup shop={shop} onShopCreated={handleShopCreated} isEdit mode="settings" />
              </div>
            </TabsContent>

            <TabsContent value="analytics">
              <Analytics />
            </TabsContent>
          </Tabs>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
